import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTheme } from '../context/ThemeContext'
import { useAuth } from '../context/AuthContext'
import { Button } from '../components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card'
import { ArrowLeft, Trophy, TrendingUp, Gift, Star, Crown, Medal } from 'lucide-react'
import DarkModeToggle from '../components/DarkModeToggle'
import axios from 'axios'
import { formatNumber } from '../utils/formatNumber'

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL
const API = `${BACKEND_URL}/api`

// Format numbers with K/M suffix
const formatCompact = (num) => {
  if (!num && num !== 0) return '0'
  const number = Number(num)
  if (isNaN(number)) return '0'
  
  if (number >= 1000000) {
    return (number / 1000000).toFixed(1).replace(/\.0$/, '') + 'M'
  }
  if (number >= 1000) {
    return (number / 1000).toFixed(1).replace(/\.0$/, '') + 'K'
  }
  return number.toString()
}

export default function Leaderboard() {
  const navigate = useNavigate()
  const { isDarkMode } = useTheme()
  const { token } = useAuth()
  const [activeTab, setActiveTab] = useState('received') // 'received', 'given', 'earned'
  const [leaderboardData, setLeaderboardData] = useState([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    fetchLeaderboard(activeTab)
  }, [activeTab])

  useEffect(() => {
    // Check user's leaderboard rank if logged in
    if (token) {
      checkMyRank()
    }
  }, [token])

  const checkMyRank = async () => {
    try {
      await axios.post(`${API}/leaderboard/check-my-rank`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      })
    } catch (err) {
      console.error('Error checking rank:', err)
    }
  }

  const fetchLeaderboard = async (type) => {
    setLoading(true)
    try {
      let endpoint = ''
      if (type === 'received') endpoint = '/leaderboard/respect-received'
      if (type === 'given') endpoint = '/leaderboard/respect-given'
      if (type === 'earned') endpoint = '/leaderboard/respect-earned'

      const response = await axios.get(`${API}${endpoint}`)
      setLeaderboardData(response.data.leaderboard || [])
    } catch (err) {
      console.error('Error fetching leaderboard:', err)
      setLeaderboardData([])
    } finally {
      setLoading(false)
    }
  }

  const getRankIcon = (rank) => {
    if (rank === 1) return <Crown className="w-5 h-5 sm:w-6 sm:h-6 text-yellow-400" />
    if (rank === 2) return <Medal className="w-5 h-5 sm:w-6 sm:h-6 text-gray-400" />
    if (rank === 3) return <Medal className="w-5 h-5 sm:w-6 sm:h-6 text-amber-600" />
    return <span className={`text-sm sm:text-base font-bold ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>#{rank}</span>
  }

  const getTabConfig = (tab) => {
    const configs = {
      received: {
        title: 'Respect Received',
        icon: Trophy,
        color: 'indigo',
        description: 'Top users by total respect received',
        valueKey: 'total_respect',
        valueLabel: 'Respect'
      },
      given: {
        title: 'Respect Given',
        icon: Gift,
        color: 'purple',
        description: 'Top users by bonuses earned from giving respect',
        valueKey: 'total_bonus',
        valueLabel: 'Bonus'
      },
      earned: {
        title: 'Respect Earned',
        icon: Star,
        color: 'pink',
        description: 'Top users by voter rewards from public panel',
        valueKey: 'total_earned',
        valueLabel: 'Earned'
      }
    }
    return configs[tab]
  }

  const config = getTabConfig(activeTab)
  const IconComponent = config.icon

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-gray-900' : 'bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50'} py-8 px-4`}>
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <Button onClick={() => navigate('/home')} variant="ghost">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Button>
          <DarkModeToggle />
        </div>

        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <TrendingUp className={`w-10 h-10 text-${config.color}-600`} />
            <h1 className={`text-4xl font-bold ${isDarkMode ? 'text-gray-100' : 'text-gray-800'}`} style={{fontFamily: 'Space Grotesk, sans-serif'}}>
              Leaderboard
            </h1>
          </div>
          <p className={`text-lg ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            Celebrate the top contributors in the Public Respect community
          </p>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-3 mb-6 justify-center">
          <Button
            onClick={() => setActiveTab('received')}
            variant={activeTab === 'received' ? 'default' : 'outline'}
            className={`${activeTab === 'received' ? 'bg-indigo-600 hover:bg-indigo-700' : ''}`}
          >
            <Trophy className="w-4 h-4 mr-2" />
            Respect Received
          </Button>
          <Button
            onClick={() => setActiveTab('given')}
            variant={activeTab === 'given' ? 'default' : 'outline'}
            className={`${activeTab === 'given' ? 'bg-purple-600 hover:bg-purple-700' : ''}`}
          >
            <Gift className="w-4 h-4 mr-2" />
            Respect Given
          </Button>
          <Button
            onClick={() => setActiveTab('earned')}
            variant={activeTab === 'earned' ? 'default' : 'outline'}
            className={`${activeTab === 'earned' ? 'bg-pink-600 hover:bg-pink-700' : ''}`}
          >
            <Star className="w-4 h-4 mr-2" />
            Respect Earned
          </Button>
        </div>

        {/* Leaderboard Card */}
        <Card className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white/90 border-purple-100'} backdrop-blur-sm`}>
          <CardHeader>
            <CardTitle className={`text-2xl font-bold flex items-center gap-3 ${isDarkMode ? 'text-gray-100' : ''}`}>
              <IconComponent className={`w-8 h-8 text-${config.color}-600`} />
              {config.title}
            </CardTitle>
            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'} mt-2`}>
              {config.description}
            </p>
          </CardHeader>
          <CardContent className="max-h-[600px] overflow-y-auto">
            {loading ? (
              <div className="text-center py-12">
                <div className={`inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 ${
                  config.color === 'indigo' ? 'border-indigo-600' :
                  config.color === 'purple' ? 'border-purple-600' :
                  'border-pink-600'
                }`}></div>
                <p className={`mt-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Loading leaderboard...</p>
              </div>
            ) : leaderboardData.length === 0 ? (
              <div className="text-center py-12">
                <Trophy className={`w-16 h-16 mx-auto mb-4 ${isDarkMode ? 'text-gray-600' : 'text-gray-300'}`} />
                <p className={`text-lg ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  No data available yet. Be the first to earn your spot!
                </p>
              </div>
            ) : (
              <div className="space-y-1">
                {leaderboardData.map((user, index) => (
                  <div
                    key={user.wallet_address}
                    onClick={() => navigate(`/profile/${user.username}`)}
                    className={`flex items-center gap-2 sm:gap-3 p-2 sm:p-3 rounded-lg cursor-pointer transition-all hover:scale-[1.01] ${
                      index < 3
                        ? isDarkMode
                          ? 'bg-gradient-to-r from-yellow-900/30 to-orange-900/30 border border-yellow-600/40'
                          : 'bg-gradient-to-r from-yellow-50 to-amber-50 border border-yellow-400/40'
                        : isDarkMode
                        ? 'bg-gray-800 hover:bg-gray-750 border border-gray-700'
                        : 'bg-white hover:bg-gray-50 border border-gray-200'
                    }`}
                  >
                    {/* Rank */}
                    <div className="flex items-center justify-center w-8 sm:w-10">
                      {getRankIcon(user.rank)}
                    </div>

                    {/* Profile Image */}
                    <div className="flex-shrink-0">
                      {user.profile_image ? (
                        <img
                          src={user.profile_image}
                          alt={user.username}
                          className="w-8 h-8 sm:w-10 sm:h-10 rounded-full object-cover border-2 border-indigo-600"
                        />
                      ) : (
                        <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-gradient-to-br ${
                          config.color === 'indigo' ? 'from-indigo-400 to-indigo-600' :
                          config.color === 'purple' ? 'from-purple-400 to-purple-600' :
                          'from-pink-400 to-pink-600'
                        } flex items-center justify-center text-white font-bold text-xs sm:text-sm`}>
                          {user.username.substring(0, 2).toUpperCase()}
                        </div>
                      )}
                    </div>

                    {/* User Info */}
                    <div className="flex-1 min-w-0">
                      <div className={`font-semibold text-sm sm:text-base ${isDarkMode ? 'text-gray-100' : 'text-gray-900'} truncate`}>
                        {user.username}
                      </div>
                      <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'} truncate hidden sm:block`}>
                        {user.name}
                      </div>
                    </div>

                    {/* Value */}
                    <div className="text-right flex-shrink-0">
                      <div className={`text-lg sm:text-xl font-bold ${
                        config.color === 'indigo' ? 'text-indigo-600' :
                        config.color === 'purple' ? 'text-purple-600' :
                        'text-pink-600'
                      } ${isDarkMode && (
                        config.color === 'indigo' ? 'text-indigo-400' :
                        config.color === 'purple' ? 'text-purple-400' :
                        'text-pink-400'
                      )}`}>
                        {formatCompact(user[config.valueKey])}
                      </div>
                      <div className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                        {config.valueLabel}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Info Box */}
        <div className={`mt-6 p-4 rounded-lg ${isDarkMode ? 'bg-blue-900/30 border border-blue-700' : 'bg-blue-50 border border-blue-200'}`}>
          <p className={`text-sm ${isDarkMode ? 'text-blue-200' : 'text-blue-800'}`}>
            <strong>Note:</strong> Leaderboards are updated in real-time and show the top 100 users in each category.
            Keep earning respect to climb the ranks!
          </p>
        </div>

        {/* Campaign Announcement */}
        <div className={`mt-6 p-6 rounded-xl ${isDarkMode ? 'bg-gradient-to-br from-yellow-900/40 to-orange-900/40 border-2 border-yellow-600' : 'bg-gradient-to-br from-yellow-50 to-orange-50 border-2 border-yellow-400'} shadow-lg`}>
          <div className="flex items-center gap-3 mb-4">
            <Trophy className={`w-8 h-8 ${isDarkMode ? 'text-yellow-400' : 'text-yellow-600'}`} />
            <h3 className={`text-2xl font-bold ${isDarkMode ? 'text-yellow-200' : 'text-yellow-800'}`}>
              $2,000,000 USDT Prize Pool — Launching Offer!
            </h3>
          </div>
          
          <div className="space-y-4">
            {/* Prize 1 */}
            <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-yellow-900/30' : 'bg-yellow-100'}`}>
              <div className={`font-bold text-lg mb-2 ${isDarkMode ? 'text-yellow-200' : 'text-yellow-900'}`}>
                1. First 100 humans with most RECEIVED respect (1 Million and above)
              </div>
              <div className={`text-xl font-bold ${isDarkMode ? 'text-green-400' : 'text-green-700'}`}>
                → $10,000 USDT each ($1,000,000 total)
              </div>
            </div>

            {/* Prize 2 */}
            <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-purple-900/30' : 'bg-purple-100'}`}>
              <div className={`font-bold text-lg mb-2 ${isDarkMode ? 'text-purple-200' : 'text-purple-900'}`}>
                2. First 100 humans with most GIVEN respect (500K and above)
              </div>
              <div className={`text-xl font-bold ${isDarkMode ? 'text-green-400' : 'text-green-700'}`}>
                → $5,000 USDT each ($500,000 total)
              </div>
            </div>

            {/* Prize 3 */}
            <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-pink-900/30' : 'bg-pink-100'}`}>
              <div className={`font-bold text-lg mb-2 ${isDarkMode ? 'text-pink-200' : 'text-pink-900'}`}>
                3. First 100 humans with most earned by HONEST VOTING (250K and above)
              </div>
              <div className={`text-xl font-bold ${isDarkMode ? 'text-green-400' : 'text-green-700'}`}>
                → $5,000 USDT each ($500,000 total)
              </div>
            </div>

            {/* Rules */}
            <div className={`p-4 rounded-lg border-2 ${isDarkMode ? 'bg-gray-800 border-red-600' : 'bg-red-50 border-red-400'}`}>
              <div className={`font-bold mb-2 ${isDarkMode ? 'text-red-300' : 'text-red-800'}`}>
                Rules (permanent):
              </div>
              <ul className={`list-disc list-inside space-y-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                <li>Must be verified</li>
                <li>Must have brought at least 1 PR</li>
                <li>Race ends when 30M PR is fully distributed & Opening Public Market for pure peer-to-peer transactions</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
